/**
 * Export Service
 * Core export logic coordinating CSV and PDF export functionality
 * 
 * This service provides a unified interface for exporting analytics data
 * in various formats with consistent handling of data transformation,
 * chart capture, and file generation.
 */

import { exportToCsv, exportMultipleToCsv, generateCsvString } from "./CsvExporter";
import { exportToPdf, exportTableToPdf, captureChartImage, exportVisualToPdf } from "./PdfExporter";
import {
    ExportableData,
    ExportableChart,
    ExportPayload,
    ExportFormat,
    ExportContext,
    CsvExportOptions,
    PdfExportOptions
} from "./types";
import Logger from "../../utils/Logger";
import moment from "moment";

/**
 * Export analytics data in the specified format
 */
export async function exportAnalytics(
    context: ExportContext,
    format: ExportFormat,
    options?: CsvExportOptions | PdfExportOptions
): Promise<void> {
    Logger.log("info", "ExportService", `Starting ${format.toUpperCase()} export for ${context.pageTitle}`);

    try {
        // For PDF visual mode, capture the container as a screenshot
        if (format === "pdf" && (options as PdfExportOptions)?.visualMode && context.getContainerRef) {
            const container = context.getContainerRef();
            if (container) {
                await exportVisualToPdf(
                    container,
                    context.pageTitle,
                    context.filters,
                    options as PdfExportOptions
                );
                Logger.log("info", "ExportService", "Visual PDF export completed successfully");
                return;
            }
        }

        const data = context.getData();

        if (!data || data.length === 0) {
            Logger.log("warn", "ExportService", "No data available to export");
            throw new Error("No data available to export");
        }

        // Add metadata to each data set
        const enrichedData = data.map(d => ({
            ...d,
            metadata: {
                ...d.metadata,
                generatedAt: moment().toISOString(),
                filters: context.filters,
                pageKey: context.pageKey,
                customerSlug: context.customerSlug
            }
        }));

        if (format === "csv") {
            if (enrichedData.length === 1) {
                exportToCsv(enrichedData[0], options as CsvExportOptions);
            } else {
                exportMultipleToCsv(enrichedData, options as CsvExportOptions);
            }
        } else if (format === "pdf") {
            // Capture charts if available
            const charts: ExportableChart[] = [];
            
            if (context.getChartRefs) {
                const chartElements = context.getChartRefs();
                
                for (let i = 0; i < chartElements.length; i++) {
                    const element = chartElements[i];
                    try {
                        const imageDataUrl = await captureChartImage(element);
                        charts.push({
                            title: enrichedData[i]?.title || `Chart ${i + 1}`,
                            type: "bar", // Default type
                            imageDataUrl
                        });
                    } catch (error) {
                        Logger.log("warn", "ExportService", `Failed to capture chart ${i + 1}`, error);
                    }
                }
            }

            const payload: ExportPayload = {
                title: context.pageTitle,
                generatedAt: moment().toISOString(),
                tables: enrichedData,
                charts,
                filters: context.filters
            };

            await exportToPdf(payload, options as PdfExportOptions);
        }

        Logger.log("info", "ExportService", `${format.toUpperCase()} export completed successfully`);
    } catch (error) {
        Logger.log("error", "ExportService", `Export failed: ${error}`);
        throw error;
    }
}

/**
 * Export a single data table
 */
export function exportTable(
    data: ExportableData,
    format: ExportFormat,
    options?: CsvExportOptions | PdfExportOptions
): Promise<void> {
    if (format === "csv") {
        exportToCsv(data, options as CsvExportOptions);
        return Promise.resolve();
    } else {
        return exportTableToPdf(data, options as PdfExportOptions);
    }
}

/**
 * Generate export preview data (CSV string or PDF preview info)
 */
export function getExportPreview(
    data: ExportableData,
    format: ExportFormat,
    maxRows = 5
): { preview: string; totalRows: number } {
    const limitedData: ExportableData = {
        ...data,
        rows: data.rows.slice(0, maxRows)
    };

    if (format === "csv") {
        return {
            preview: generateCsvString(limitedData),
            totalRows: data.rows.length
        };
    } else {
        // For PDF, return a summary
        const columnNames = data.columns.map(c => c.label).join(", ");
        return {
            preview: `PDF Export Preview\n\nTitle: ${data.title}\nColumns: ${columnNames}\nShowing ${Math.min(maxRows, data.rows.length)} of ${data.rows.length} rows`,
            totalRows: data.rows.length
        };
    }
}

/**
 * Validate export data before processing
 */
export function validateExportData(data: ExportableData): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.title) {
        errors.push("Export title is required");
    }

    if (!data.columns || data.columns.length === 0) {
        errors.push("At least one column is required");
    }

    if (!data.rows) {
        errors.push("Rows array is required");
    }

    // Check for column key consistency
    if (data.columns && data.rows && data.rows.length > 0) {
        const sampleRow = data.rows[0];
        
        const missingKeys = data.columns
            .filter(col => !Object.prototype.hasOwnProperty.call(sampleRow, col.key))
            .map(col => col.key);

        if (missingKeys.length > 0) {
            errors.push(`Missing column keys in data: ${missingKeys.join(", ")}`);
        }
    }

    return {
        valid: errors.length === 0,
        errors
    };
}

/**
 * Get supported export formats for a given data type
 */
export function getSupportedFormats(): ExportFormat[] {
    return ["csv", "pdf"];
}

// Re-export types and utilities for convenience
export * from "./types";
export { captureChartImage } from "./PdfExporter";
